import { ApiError } from "./ApiError";

export type ApiClientOptions = {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  defaultRetries?: number;
  timeoutMs?: number;
  headers?: Record<string, string>;
  noKeepAlive?: boolean;
};

export const DEFAULT_API_TIMEOUT_MS = 15_000;
export const MAX_API_TIMEOUT_MS = 120_000;

const responseStatuses = new WeakMap<object, number>();

export function getApiResponseStatus(response: unknown): number | undefined {
  return typeof response === "object" && response !== null
    ? responseStatuses.get(response)
    : undefined;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly defaultRetries: number;
  private readonly timeoutMs: number;
  private readonly headers: Record<string, string>;
  private readonly noKeepAlive: boolean;

  constructor(opts: ApiClientOptions) {
    if (!opts.baseUrl) {
      throw new Error("ApiClient: baseUrl is required and cannot be empty");
    }
    this.baseUrl = opts.baseUrl.replace(/\/+$/, "");
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    this.defaultRetries = opts.defaultRetries ?? 3;
    this.timeoutMs = normalizeTimeoutMs(opts.timeoutMs);
    this.headers = opts.headers ?? {};
    this.noKeepAlive = opts.noKeepAlive === true;
  }

  async request<T>(
    path: string,
    init: RequestInit = {},
    retries = this.defaultRetries,
  ): Promise<T> {
    const url = `${this.baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

    // Build headers once, but clone per attempt.
    const baseHeaders = new Headers(init.headers ?? {});
    // Merge instance headers
    for (const [key, value] of Object.entries(this.headers)) {
      if (!baseHeaders.has(key)) {
        baseHeaders.set(key, value);
      }
    }
    if (!baseHeaders.has("Content-Type") && init.body != null) {
      baseHeaders.set("Content-Type", "application/json; charset=utf-8");
    }
    baseHeaders.set("X-Client-Platform-Hint", "web");

    // Server-side (BFF): disable keep-alive to prevent stale connection reuse
    // from the Node.js undici connection pool. Without this, a pooled connection
    // the gateway already closed causes ECONNRESET with no server-side logs.
    if (this.noKeepAlive || !("window" in globalThis)) {
      baseHeaders.set("Connection", "close");
    }

    // CSRF double-submit: attach csrf cookie value as header on unsafe methods
    const method = (init.method ?? "GET").toUpperCase();
    if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
      const csrfToken = getCsrfCookie();
      if (csrfToken) {
        baseHeaders.set("X-CSRF-Token", csrfToken);
      }
    }

    for (let attempt = 0; attempt <= retries; attempt++) {
      const headers = new Headers(baseHeaders);
      const requestAbort = composeAbortSignal(init.signal, this.timeoutMs);

      let res: Response;
      try {
        res = await this.fetchImpl(url, {
          ...init,
          headers,
          credentials: init.credentials ?? "include",
          signal: requestAbort.signal,
        });
      } catch (networkErr) {
        const timedOut = requestAbort.didTimeout();
        const callerAborted = init.signal?.aborted === true;
        requestAbort.cleanup();

        if (attempt === retries || callerAborted) {
          const message = timedOut
            ? `Request timed out after ${this.timeoutMs}ms.`
            : networkErrorMessage(networkErr, init.signal);
          throw new ApiError(message, {
            kind: timedOut ? "timeout" : "network",
            cause: networkErr,
          });
        }
        const delayMs = computeDelayMs(null, attempt);
        await sleep(delayMs);
        continue;
      } finally {
        requestAbort.cleanup();
      }

      // Success (or non-retryable error)
      const retryable =
        res.status === 429 || (res.status >= 500 && res.status <= 599);
      if (!retryable) {
        if (!res.ok) {
          const { payload, text } = await safeBody(res);
          throw new ApiError(`HTTP ${res.status}: ${text || res.statusText}`, {
            kind: "http",
            outerStatus: res.status,
            payload,
          });
        }
        return await parseSuccessResponse<T>(res);
      }

      // Retryable, but no retries left
      if (attempt === retries) {
        const { payload, text } = await safeBody(res);
        throw new ApiError(
          `HTTP ${res.status} after ${retries} retries: ${text || res.statusText}`,
          {
            kind: "http",
            outerStatus: res.status,
            payload,
          },
        );
      }

      // Wait before retry (Retry-After wins)
      const delayMs = computeDelayMs(res, attempt);
      await sleep(delayMs);
    }

    // unreachable
    throw new Error("ApiClient: request retry loop fell through");
  }
}

function normalizeTimeoutMs(value: number | undefined): number {
  const timeoutMs = value ?? DEFAULT_API_TIMEOUT_MS;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new Error("ApiClient: timeoutMs must be a positive finite number");
  }
  return Math.min(Math.floor(timeoutMs), MAX_API_TIMEOUT_MS);
}

function networkErrorMessage(
  error: unknown,
  callerSignal: AbortSignal | null | undefined,
): string {
  if (callerSignal?.aborted && callerSignal.reason instanceof Error) {
    return callerSignal.reason.message;
  }
  return error instanceof Error ? error.message : "Network request failed.";
}

function composeAbortSignal(
  callerSignal: AbortSignal | null | undefined,
  timeoutMs: number,
): {
  signal: AbortSignal;
  didTimeout: () => boolean;
  cleanup: () => void;
} {
  const controller = new AbortController();
  let timedOut = false;

  const abortFromCaller = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) {
    abortFromCaller();
  } else {
    callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
  }

  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort(new Error(`Request timed out after ${timeoutMs}ms.`));
  }, timeoutMs);

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    cleanup: () => {
      clearTimeout(timeout);
      callerSignal?.removeEventListener("abort", abortFromCaller);
    },
  };
}

function computeDelayMs(res: Response | null, attempt: number): number {
  if (res) {
    const ra = res.headers.get("Retry-After");
    if (ra) {
      const secs = Number(ra);
      if (!Number.isNaN(secs) && secs >= 0) return secs * 1000;
      const dt = Date.parse(ra);
      if (!Number.isNaN(dt)) return Math.max(0, dt - Date.now());
    }
  }
  // exponential backoff with jitter
  const base = 150 * Math.pow(2, attempt);
  const jitter = Math.floor(Math.random() * 150);
  return Math.min(base + jitter, 2000);
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

async function parseSuccessResponse<T>(res: Response): Promise<T> {
  try {
    const payload = (await res.json()) as T;
    if (typeof payload === "object" && payload !== null) {
      responseStatuses.set(payload, res.status);
    }
    return payload;
  } catch (cause) {
    throw new ApiError(`Invalid JSON response from API (HTTP ${res.status}).`, {
      kind: "protocol",
      outerStatus: res.status,
      cause,
    });
  }
}

async function safeBody(
  res: Response,
): Promise<{ payload: unknown; text: string }> {
  try {
    const text = await res.text();
    if (!text) return { payload: null, text };

    try {
      return { payload: JSON.parse(text), text };
    } catch {
      return { payload: text, text };
    }
  } catch {
    return { payload: null, text: "" };
  }
}

function getCsrfCookie(): string | undefined {
  const browserDocument = (globalThis as { document?: { cookie: string } })
    .document;
  if (!browserDocument) return undefined;

  const encodedToken =
    browserDocument.cookie.match(/(?:^|; )csrf=([^;]*)/)?.[1];
  return encodedToken === undefined
    ? undefined
    : decodeURIComponent(encodedToken);
}
